import { useEffect, useRef, useState } from 'react';

import { EVENTS_RPC, LIST_BUCKET, eventsRpcArgs } from '../list-queries';
import { getPrefetched } from '../static-prefetch';
import { supabase, type DbEvent } from '../supabase';

export type EventsQuery = {
  lat?: number;
  lng?: number;
  radiusM?: number;
  from?: Date;
  until?: Date;
  /**
   * Forward window in days, used when `until` isn't given. A plain number
   * rather than a Date so it can sit in a query object without making the
   * effect key change on every render — see list-queries.ts.
   */
  untilDays?: number;
  category?: string;
  city?: string;
  limit?: number;
};

/**
 * `seedKey` opts a call site into the build-time seed: on a page whose list was
 * prefetched at export time, the first render — in Node during the static
 * export AND in the browser during hydration — returns the baked rows instead
 * of an empty array, so the cards are in the static HTML as real links. Call
 * sites that pass no key behave exactly as before.
 */
export function useEvents(query: EventsQuery = {}, seedKey?: string) {
  // Read once: on the client this touches the DOM (the baked <script> the page
  // shipped), which shouldn't repeat on every render.
  const [seed] = useState(() => getPrefetched<DbEvent[]>(LIST_BUCKET, seedKey));
  const [events, setEvents] = useState<DbEvent[]>(seed ?? []);
  const [loading, setLoading] = useState(seed === undefined);
  const [error, setError] = useState<string | null>(null);

  const key = JSON.stringify(query);
  // The one key whose first fetch must NOT show a loading state, because we
  // already have its rows on screen from the seed. Cleared after that first
  // run, so a later filter change still gets normal loading feedback.
  const seededKey = useRef(seed !== undefined ? key : null);

  useEffect(() => {
    let cancelled = false;
    const isSeededFirstRun = seededKey.current === key;
    seededKey.current = null;

    // A seeded list is already complete for what the page renders (see the
    // trimmers in the app's src/lib/detail/list-prefetch.ts), and it's no staler than the
    // static HTML around it — both come from the same daily build. Refetching
    // it on load would pull the same rows over the network a second time for
    // no visible change. Later runs (a city filter) fetch normally.
    if (isSeededFirstRun) return;

    setLoading(true);
    setError(null);

    supabase
      .rpc(EVENTS_RPC, eventsRpcArgs(query))
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setError(error.message);
          setEvents([]);
        } else {
          setEvents((data ?? []) as DbEvent[]);
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { events, loading, error };
}

// Exact count of shows matching the date/city/category filters — what the
// lists show, one per recurring show. The app doesn't use the geo radius (no
// lat/lng passed), so the count RPC skips it and isn't bounded by any limit.
export function useEventsCount(query: EventsQuery = {}) {
  const [count, setCount] = useState<number | null>(null);

  const key = JSON.stringify(query);

  useEffect(() => {
    let cancelled = false;
    setCount(null);

    const args = eventsRpcArgs(query);
    supabase
      .rpc('event_shows_count', {
        p_from: args.p_from,
        p_until: args.p_until,
        p_category: args.p_category,
        p_city: args.p_city,
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        setCount(error ? null : Number(data ?? 0));
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return count;
}
